import React, { useState } from 'react';
import { Alert, Button, Card, Table, UncontrolledTooltip } from 'reactstrap';
import { useUnit } from 'hooks/units';
import { formatHHmmDDMMYYYY } from 'helpers/format-time';
import ListTableRow from 'components/Table/ListTableRow';
import { bytesToSize } from 'helpers/number';
import { Download } from 'react-feather';
import { Spin } from 'antd';
import { downloadFile } from 'helpers';
import { STATUS_FILES_UNIT } from 'constants/constants';
import { LoadingOutlined } from '@ant-design/icons';
import { getThemeColor } from 'store/settings';
import { useSelector } from 'react-redux';
import { showToastError } from '@core/components/toastify/ToastTypes';

const ContentFiles = () => {
  const unitSelected = useUnit();
  const themeColor = useSelector(getThemeColor);
  const [isLoading, setIsLoading] = useState(false);

  // Extract files from unit configuration
  const unitFiles =
    unitSelected?.config?.unitFiles?.reduce(
      (acc, item) => acc.concat(item.files),
      [],
    ) || [];

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 30, color: themeColor }} spin />
  );

  const isFileUnavailable =
    unitSelected?.statusFile === STATUS_FILES_UNIT.EXPIRED ||
    unitSelected?.statusFile === STATUS_FILES_UNIT.UPCOMING;

  const handleDownloadFile = async (file) => {
    try {
      setIsLoading(true);

      if (unitSelected?.statusFile) {
        showToastError('This file is currently not available to download');
        setIsLoading(false);
        return;
      }

      await downloadFile(file?.path, file?.title);
    } catch (error) {
      console.error('File download failed', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFileRow = (file, index) => (
    <tr key={index}>
      <td className="text-left">{index + 1}</td>
      <td className="text-left">{file?.title}</td>
      <td className="text-left">{bytesToSize(file?.size)}</td>
      <td className="text-center">
        <Button
          className="btn-icon px-05 py-05 upload-btn-top"
          color="flat-dark"
          id={`id-${file?._id}`}
          onClick={() => handleDownloadFile(file)}
        >
          <Download size={15} />
          <UncontrolledTooltip placement="top" target={`id-${file?._id}`}>
            Download
          </UncontrolledTooltip>
        </Button>
      </td>
    </tr>
  );

  const renderFilesTable = (files) => (
    <Table className="table-header-custom" responsive>
      <thead>
        <tr>
          <th className="text-left">No.</th>
          <th className="text-left">Name</th>
          <th className="text-left">Size</th>
          {unitSelected?.statusFile !== STATUS_FILES_UNIT.PERMISSION && (
            <th>Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        <ListTableRow col={4} length={files.length}>
          {files.map(renderFileRow)}
        </ListTableRow>
      </tbody>
    </Table>
  );

  const renderAlertMessage = () => (
    <Alert color="warning">
      <div className="alert-body">
        <span>This file is currently not available to download.</span>
      </div>
    </Alert>
  );

  const renderPublishInfo = () => (
    <>
      <h5>
        <label className="font-medium-3">
          Publish date:
          <strong className="font-medium-2">
            {formatHHmmDDMMYYYY(unitSelected?.time?.start)}
          </strong>
        </label>
        {unitSelected?.time?.end && (
          <>
            {' - '}
            <label className="font-medium-3">
              Expiry date:
              <strong className="font-medium-2">
                {formatHHmmDDMMYYYY(unitSelected?.time?.end)}
              </strong>
            </label>
          </>
        )}
      </h5>
      <p className="font-medium-2 mt-md-2 mt-2 mx-lg-5 px-lg-4 mx-md-4 px-md-2 mx-sm-3 px-sm-1 mx-2">
        Please download the files below within the time given. Once expired, the
        files are no longer available for download.
      </p>
    </>
  );

  const renderCardContent = () => {
    if (!unitFiles.length && !unitSelected?.files?.length) {
      return (
        <Alert color="warning">
          <div className="alert-body">
            <span>No available files</span>
          </div>
        </Alert>
      );
    }

    return (
      <>
        {isFileUnavailable ? renderAlertMessage() : renderPublishInfo()}
        <div className="overflow-x-auto">
          <Spin indicator={antIcon} spinning={isLoading}>
            {unitFiles.length
              ? renderFilesTable(unitFiles)
              : renderFilesTable(unitSelected?.files || [])}
          </Spin>
        </div>
      </>
    );
  };

  return (
    <Card className="mt-md-2 mt-1" style={{ zIndex: 1 }}>
      <div className="text-center px-md-2 pt-md-3 p-sm-1 px-05 py-1">
        {renderCardContent()}
      </div>
    </Card>
  );
};

export default ContentFiles;
